import { useState , useEffect } from "react";
import {
  ShieldCheck,
  Copy,
  Download,
  CheckCircle,
  AlertTriangle,
  Gauge,
  FileText,
  Info,
  Link,
  Lightbulb,
  Image as ImageIcon,
} from "lucide-react";

const ResponseBox = ({ response, handleDownload, t, url }) => {
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(null);
   useEffect(() => {
  if (url) {
    const fullUrl = `${process.env.REACT_APP_API_URL}${url}`;
    setPreview(fullUrl);
  }
}, [url]);
if (!response) return null;
 const data = response?.response ?? response ?? {};
  const handleCopy = () => {
    navigator.clipboard.writeText(
      typeof response === "string"
        ? response
        : JSON.stringify(response, null, 2),
    );

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  // ---------------------
  // CAS TEXTE
  // ---------------------
  if (typeof data === "string") {
    return (
      <div className="mt-6 flex justify-center items-center">
        <div className="max-w-2xl w-full bg-white border rounded-2xl shadow-md p-4 relative">
          <div className="flex justify-between items-center mb-4">
            <strong className="text-gray-700 flex items-center gap-2">
              <ShieldCheck size={18} />
              {t("chatbotResponse")}
            </strong>

            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 text-xs px-3 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
              >
                <Copy size={14} />
                {copied ? "Copié !" : "Copier"}
              </button>

              <button
                onClick={handleDownload}
                className="flex items-center gap-1 text-xs px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                <Download size={14} />
                {t("localpdf")}
              </button>
            </div>
          </div>

          <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap">
            {data}
          </p>
        </div>
      </div>
    );
  }

  // ---------------------
  // CAS JSON IMAGE
  // ---------------------
  const score = Number(data?.scoreConfiance ?? 0);

  const verdictColor =
    data?.verdict === "Authentique"
      ? "bg-green-100 text-green-700"
      : data?.verdict === "Généré par IA"
        ? "bg-red-100 text-red-700"
        : "bg-gray-100 text-gray-700";

  return (
    <div className="mt-6 flex justify-center flex-wrap items-center">
      <div className=" flex  gap-5 max-w-5xl w-full bg-white border rounded-2xl shadow-md p-5 relative">
        {/* HEADER */}
        <div className="w-full max-w-xl">
          <div className="flex items-center gap-2 mb-2 text-gray-700 font-medium">
            <ImageIcon size={16} />
            Image analysée
          </div>

          <a
            href={preview}
            target="_blank"
            rel="noreferrer"
            className="block"
          >
            <img
              src={preview}
              alt="Image analysée"
              className="w-full h-64 md:h-72 object-contain rounded-xl border border-gray-200 bg-gray-50 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300 cursor-pointer"
            />
          </a>
        </div>
        <div className="">
          <div className="flex justify-between items-center mb-4">
            <strong className="text-gray-700 flex items-center gap-2">
              <ShieldCheck size={18} />
              {t("chatbotResponse")}
            </strong>

            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 text-xs px-3 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
              >
                <Copy size={14} />
                {copied ? "Copié !" : "Copier"}
              </button>

              <button
                onClick={handleDownload}
                className="flex items-center gap-1 text-xs px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                <Download size={14} />
                {t("localpdf")}
              </button>
            </div>
          </div>

          {/* VERDICT */}
          <div className="flex justify-between items-center mb-4">
            <span
              className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${verdictColor}`}
            >
              {data.verdict === "Authentique" ? (
                <CheckCircle size={14} />
              ) : (
                <AlertTriangle size={14} />
              )}
              {data.verdict ?? "N/A"}
            </span>

            <span className="flex items-center gap-1 text-sm text-gray-500">
              <Gauge size={15} />
              {data.confiance ?? "N/A"} ({score}%)
            </span>
          </div>

          {/* BARRE DE SCORE */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-5 overflow-hidden">
            <div
              className={`h-3 rounded-full transition-all duration-700 ${
                score > 70
                  ? "bg-green-500"
                  : score > 40
                    ? "bg-yellow-500"
                    : "bg-red-500"
              }`}
              style={{ width: `${score}%` }}
            />
          </div>

          {/* ANALYSE */}
          {data.analyse && (
            <div className="bg-gray-50 rounded-xl p-3 mb-3">
              <div className="flex items-center gap-2 mb-2 text-gray-700 font-medium">
                <FileText size={16} />
                {t("analyse")}
              </div>

              <p className="text-gray-700 text-sm leading-relaxed">
                {data.analyse}
              </p>
            </div>
          )}

          {/* FIABILITÉ */}
          {/* {data.fiabiliteSources && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-3">
              <div className="flex items-center gap-2 mb-1 text-blue-700 font-medium">
                <ShieldCheck size={16} />
                {t("fiabilite")}
              </div>

              <p className="text-blue-700 text-sm">{data.fiabiliteSources}</p>
            </div>
          )} */}

          {/* EXPLICATION */}
          {data.explication && (
            <div className="bg-slate-50 rounded-xl p-3 mb-3">
              <div className="flex items-center gap-2 mb-1 text-slate-700 font-medium">
                <Info size={16} />
                {t("explication")}
              </div>

              <p className="text-slate-600 text-sm">{data.explication}</p>
            </div>
          )}

          {/* SOURCES */}
          {Array.isArray(data.liensSources) && data.liensSources.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2 text-gray-700 font-medium">
                <Link size={16} />
                {t("linked")}
              </div>

              <ul className="space-y-2">
                {data.liensSources.map((link, i) => (
                  <li key={i}>
                    <a
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 text-xs hover:underline break-all"
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* RECOMMANDATION */}
          {data.recommandation && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1 text-amber-700 font-medium">
                <Lightbulb size={16} />
                {t("Recommendation")}
              </div>

              <p className="text-amber-700 text-sm italic">
                {data.recommandation}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResponseBox;
